import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiGet, apiPut } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import { Target, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

// The disposition kinds CIVIX can map to Google Ads conversion actions, with
// human labels + the default placeholder values from the integration spec.
const KINDS = [
  { key: 'sold', label: 'Sold Policy', hint: 'Fires when a lead is marked SOLD. Value is the real premium when known, else this placeholder.', defaultValue: 300 },
  { key: 'quoted', label: 'Quoted Lead', hint: 'Fires when a lead transitions to QUOTED — an upper-funnel signal for Smart Bidding.', defaultValue: 25 },
  { key: 'call_qualified', label: 'Qualified Call', hint: 'Fires on a completed call of 60s or longer.', defaultValue: 10 },
  { key: 'form_submission', label: 'Form Submission', hint: 'Optional — fires on inbound form submission.', defaultValue: 5 },
];

export default function IntegrationsGoogleAds() {
  const qc = useQueryClient();

  const { data: config } = useQuery({
    queryKey: ['integrations-google-ads'],
    queryFn: () => apiGet('/integrations/google-ads'),
    refetchInterval: 15_000,
  });
  const { data: convActions } = useQuery({
    queryKey: ['integrations-google-ads-conversion-actions'],
    queryFn: () => apiGet('/integrations/google-ads/conversion-actions'),
    enabled: !!config?.connected,
  });

  const connected = !!config?.connected;
  const actions = convActions?.items || [];

  return (
    <div className="space-y-4">
      <TopBar
        title="Google Ads — Conversion Actions"
        subtitle="Map CIVIX dispositions to Google Ads offline-conversion actions"
      />

      {!config?.devTokenConfigured && (
        <div className="panel p-4 border border-yellow-500/30 bg-yellow-500/5">
          <div className="flex items-start gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5" />
            <div>
              <div className="font-medium text-yellow-200">Developer token pending</div>
              <div className="text-xs text-white/60 mt-1">
                Conversions are queued in stub mode and will upload for real once{' '}
                <code className="text-civix-cyan">GOOGLE_ADS_DEVELOPER_TOKEN</code> is approved and
                set. Your config below is saved and applied automatically when the token lands.
              </div>
            </div>
          </div>
        </div>
      )}

      {!connected && (
        <div className="panel p-4 text-sm text-white/70">
          Google Ads isn't connected yet.{' '}
          <Link to="/google-ads" className="text-civix-cyan underline inline-flex items-center gap-1">
            Connect it here <ExternalLink className="w-3 h-3" />
          </Link>{' '}
          first, then return to configure conversion actions.
        </div>
      )}

      {connected && (
        <div className="panel p-4 space-y-4">
          <div className="flex items-center gap-2 text-sm text-white/70">
            <CheckCircle2 className="w-4 h-4 text-green-400" /> Connected
            {config?.connection?.customerName && (
              <span className="text-xs text-white/40">· {config.connection.customerName}</span>
            )}
          </div>
          {KINDS.map((kind) => (
            <ActionRow
              key={kind.key}
              kind={kind}
              actions={actions}
              current={config?.conversionActions?.[kind.key]}
              onSaved={() => qc.invalidateQueries({ queryKey: ['integrations-google-ads'] })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ActionRow({ kind, actions, current, onSaved }) {
  const [enabled, setEnabled] = useState(!!current?.enabled);
  const [resourceName, setResourceName] = useState(current?.resourceName || '');
  const [value, setValue] = useState(current?.value ?? kind.defaultValue);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setEnabled(!!current?.enabled);
    setResourceName(current?.resourceName || '');
    setValue(current?.value ?? kind.defaultValue);
  }, [current, kind.defaultValue]);

  async function save() {
    setSaving(true);
    try {
      const item = actions.find((i) => i.resourceName === resourceName);
      await apiPut('/integrations/google-ads', {
        conversionActions: {
          [kind.key]: {
            enabled,
            resourceName,
            name: item?.name || current?.name || '',
            value: Number(value),
            currencyCode: 'USD',
          },
        },
      });
      toast.success(`${kind.label} saved`);
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="border-t border-white/5 pt-4 space-y-2">
      <div className="flex items-center gap-2 text-sm text-white/80">
        <Target className="w-4 h-4" /> {kind.label}
        <label className="ml-auto flex items-center gap-2 text-xs text-white/60">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          Enabled
        </label>
      </div>
      <div className="text-xs text-white/40">{kind.hint}</div>
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <label className="block text-xs text-white/60 mb-1">Conversion action</label>
          <select
            className="input w-full"
            value={resourceName}
            onChange={(e) => setResourceName(e.target.value)}
            disabled={!enabled}
          >
            <option value="">Select…</option>
            {actions.map((i) => (
              <option key={i.resourceName} value={i.resourceName}>
                {i.name} {i.category ? `· ${i.category}` : ''} {i.status ? `· ${i.status}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="w-28">
          <label className="block text-xs text-white/60 mb-1">Value (USD)</label>
          <input
            type="number"
            className="input w-full"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!enabled}
          />
        </div>
        <button
          onClick={save}
          disabled={saving || (enabled && !resourceName)}
          className="px-3 py-1.5 rounded-md bg-linear-to-r from-civix-purple to-civix-cyan text-sm font-medium disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
